import { useCallback, useContext, useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AccountContext } from '@/contexts/account'
import type { AccountContextValue } from '@/contexts/account'
import { guardScreenEditOrAlert, isScreenReadOnlyForUi } from '@/libs/hotel-pms-permission'

/**
 * 設定画面向けの read-only 判定と編集ガード。
 */
export const useSettingsEditGuard = () => {
  const { t } = useTranslation()
  const { permissions, permissionsLoaded, permissionsError } = useContext<AccountContextValue>(AccountContext)
  const isSettingsReadOnly = isScreenReadOnlyForUi(permissions, 'settings', permissionsLoaded, permissionsError)

  const guardSettingsEdit = useCallback(() => {
    return guardScreenEditOrAlert(permissions, 'settings', permissionsLoaded, permissionsError, t)
  }, [permissions, permissionsLoaded, permissionsError, t])

  return { isSettingsReadOnly, guardSettingsEdit, permissionsLoaded, permissionsError }
}

type UseSettingsEditPageGuardEffectParams = {
  redirectPath: string
  onAllowed: () => void | (() => void) | Promise<void | (() => void)>
  search?: string
}

/**
 * 設定編集画面: permissions 確定後に read-only / エラーなら一覧へリダイレクトし、
 * 編集可のときのみ onAllowed を実行する。
 */
export const useSettingsEditPageGuardEffect = ({ redirectPath, onAllowed, search }: UseSettingsEditPageGuardEffectParams): void => {
  const { t } = useTranslation()
  const history = useHistory()
  const location = useLocation()
  const { permissions, permissionsLoaded, permissionsError } = useContext<AccountContextValue>(AccountContext)
  const isSettingsReadOnly = isScreenReadOnlyForUi(permissions, 'settings', permissionsLoaded, permissionsError)

  useEffect(() => {
    if (!permissionsLoaded) return

    const redirectSearch = search ?? location.search
    if (permissionsError) {
      alert(t('HotelPms.Error.contextMissing'))
      history.replace({ pathname: redirectPath, search: redirectSearch })
      return
    }
    if (isSettingsReadOnly) {
      alert(t('HotelPms.Error.readOnly'))
      history.replace({ pathname: redirectPath, search: redirectSearch })
      return
    }

    const result = onAllowed()
    if (result instanceof Promise) {
      void result
      return
    }
    if (typeof result === 'function') {
      return result
    }
    // onAllowed は permissions 確定時のみ実行したいため依存から除外
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissionsLoaded, permissionsError, isSettingsReadOnly, redirectPath, search, location.search, history, t])
}
